import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import Pagination from "./Pagination";
import PriceRangePicker from "./PriceRangePicker";
import API_BASE_URL from "../Config";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { ShoppingCart } from 'lucide-react';
import "./styles/ProductList.css";
import { requireLogin } from "../utils/authRedirect";
import { flyToCart } from "../utils/flyToCart";
import { getProductRatingSummary } from "../utils/productReviews";

// In your layout or any page component:
import MobileBottomNav from "./MobileBottomNav";
import "../styles/global.css";


/* ═══════════════════════════════════════════════════
   ALL STYLES — single file, no external CSS needed
═══════════════════════════════════════════════════ */
const STYLES = `
// @import url('https://fonts.googleapis.com/css2?family=inter:wght@400;500;600;700;800&display=swap');

/* ── reset ── */
.pl-wrap * { box-sizing: border-box; }

/* ── page shell ── */
.pl-wrap {
  background: #f5f8f7;
  font-family: 'inter', sans-serif;
}

/* ── layout row ── */
.pl-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

/* ════════════════════════════
   SIDEBAR
════════════════════════════ */
.pl-sidebar {
  width: 268px;
  min-width: 268px;
  flex-shrink: 0;
  position: sticky;
  top: 88px;
  max-height: calc(100vh - 108px);
  overflow-y: auto;
  overflow-x: hidden;
  background: #fff;
  border: 1px solid #eef1f6;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(12,25,41,.06);
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
  z-index: 1;
}
.pl-sidebar::-webkit-scrollbar { width: 4px; }
.pl-sidebar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

/* sidebar header */
.pl-sb-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  // padding: 16px 18px;
  // border-bottom: 1px solid #f1f5f9;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 2;
  border-radius: 16px 16px 0 0;
}
.pl-sb-title {
  font-size: .93rem;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -.01em;
  display: flex;
  align-items: center;
  gap: 7px;
}
.pl-sb-title i { color: #1E7A8C; font-size: .82rem; }
.pl-total-badge {
  background: #1E7A8C; color: #fff;
  font-size: .65rem; font-weight: 700;
  min-width: 18px; height: 18px; border-radius: 18px;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0 5px;
}
.pl-clear-btn {
  display: flex; align-items: center; gap: 5px;
  background: rgba(239,68,68,.06);
  border: 1px solid rgba(239,68,68,.15);
  color: #ef4444; border-radius: 20px;
  padding: 4px 11px;
  font-family: 'inter', sans-serif;
  font-size: .72rem; font-weight: 700;
  cursor: pointer;
  transition: background .18s;
}
.pl-clear-btn:hover { background: rgba(239,68,68,.12); }

/* active chips */
.pl-chips {
  display: flex; flex-wrap: wrap; gap: 5px;
  padding: 10px 18px;
  border-bottom: 1px solid #f1f5f9;
  background: #fbfcfe;
}
.pl-chip {
  display: inline-flex; align-items: center; gap: 4px;
  background: #e8f5f2;
  border: 1px solid rgba(30,122,140,.2);
  color: #155f6e;
  border-radius: 20px; padding: 3px 9px 3px 11px;
  font-size: .72rem; font-weight: 600;
  max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.pl-chip button {
  background: rgba(30,122,140,.15); border: none; border-radius: 50%;
  width: 15px; height: 15px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; padding: 0; color: #155f6e; flex-shrink: 0;
  transition: background .15s;
}
.pl-chip button:hover { background: rgba(30,122,140,.3); }
.pl-chip button i { font-size: .55rem; }

/* accordion section */
.pl-acc-section { border-bottom: 1px solid #f1f5f9; }
.pl-acc-section:last-child { border-bottom: none; }

.pl-acc-btn {
  display: flex; align-items: center; justify-content: space-between;
  width: 100%; padding: 13px 18px;
  background: none; border: none; cursor: pointer;
  font-family: 'inter', sans-serif;
  font-size: .78rem; font-weight: 700;
  color: #0f172a; letter-spacing: .04em;
  text-transform: uppercase;
  transition: background .15s;
}
.pl-acc-btn:hover { background: #f8fafc; }

.pl-acc-label { display: flex; align-items: center; gap: 7px; }
.pl-acc-label i { color: #1E7A8C; font-size: .72rem; width: 14px; text-align: center; }

.pl-acc-badge {
  background: #1E7A8C; color: #fff;
  font-size: .6rem; font-weight: 700;
  min-width: 16px; height: 16px; border-radius: 16px;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0 4px;
}
.pl-acc-chevron {
  color: #94a3b8;
  transition: transform .27s cubic-bezier(.4,0,.2,1), color .18s;
  display: flex; align-items: center;
}
.pl-acc-chevron.open { transform: rotate(180deg); color: #1E7A8C; }

.pl-acc-body {
  overflow: hidden;
  transition: max-height .3s cubic-bezier(.4,0,.2,1);
}
.pl-acc-inner { padding: 2px 0 10px; }

/* search inside filter */
.pl-fsearch { position: relative; padding: 0 18px; margin-bottom: 6px; }
.pl-fsearch-icon {
  position: absolute; left: 30px; top: 50%;
  transform: translateY(-50%);
  color: #94a3b8; font-size: .72rem; pointer-events: none;
}
.pl-fsearch input {
  width: 100%; padding: 8px 28px 8px 30px;
  border: 1.5px solid #e2e8f0; border-radius: 9px;
  font-family: 'inter', sans-serif;
  font-size: .8rem; font-weight: 500;
  color: #0f172a; background: #f8fafc;
  outline: none; transition: all .2s;
}
.pl-fsearch input:focus {
  border-color: #1E7A8C;
  box-shadow: 0 0 0 3px rgba(30,122,140,.08);
  background: #fff;
}
.pl-fsearch input::placeholder { color: #94a3b8; font-weight: 400; }
.pl-fsearch-clear {
  position: absolute; right: 26px; top: 50%;
  transform: translateY(-50%);
  background: #e2e8f0; border: none; border-radius: 50%;
  width: 16px; height: 16px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; padding: 0; color: #64748b; font-size: .6rem;
}

/* checkbox list */
.pl-chk-list {
  max-height: 195px; overflow-y: auto;
  padding: 2px 18px 4px;
  scrollbar-width: thin; scrollbar-color: #e2e8f0 transparent;
}
.pl-chk-list::-webkit-scrollbar { width: 3px; }
.pl-chk-list::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 3px; }

.pl-chk-item {
  display: flex; align-items: center; gap: 9px;
  padding: 6px 7px; border-radius: 7px;
  cursor: pointer; user-select: none;
  transition: background .14s;
}
.pl-chk-item:hover { background: #f8fafc; }
.pl-chk-item.active { background: #e8f5f2; }

.pl-chk-item input[type="checkbox"] { display: none; }

.pl-chk-box {
  width: 16px; height: 16px; border-radius: 4px;
  border: 2px solid #cbd5e1; background: #fff;
  flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  transition: all .18s;
}
.pl-chk-item.active .pl-chk-box { background: #1E7A8C; border-color: #1E7A8C; }

.pl-chk-lbl {
  font-size: .8rem; font-weight: 500; color: #475569;
  flex: 1; line-height: 1.3; transition: color .15s;
}
.pl-chk-item.active .pl-chk-lbl { color: #155f6e; font-weight: 600; }

.pl-chk-cnt {
  font-size: .68rem; color: #94a3b8;
  background: #f1f5f9; border-radius: 20px;
  padding: 1px 6px; font-weight: 500; flex-shrink: 0;
}
.pl-chk-item.active .pl-chk-cnt { background: rgba(30,122,140,.1); color: #1E7A8C; }

.pl-empty-msg { font-size: .78rem; color: #94a3b8; text-align: center; padding: 10px; }

/* price range */
.pl-price-wrap { padding: 4px 18px 8px; }
.pl-price-wrap .rc-slider-track { background: linear-gradient(90deg,#1E7A8C,#238495) !important; height: 5px; }
.pl-price-wrap .rc-slider-rail  { background: #e2e8f0 !important; height: 5px; }
.pl-price-wrap .rc-slider-handle {
  border: 2px solid #1E7A8C !important; background: #fff !important;
  width: 18px !important; height: 18px !important; margin-top: -7px !important;
  box-shadow: 0 2px 8px rgba(30,122,140,.2) !important; opacity: 1 !important;
}

/* ════════════════════════════
   MAIN CONTENT
════════════════════════════ */
.pl-main { flex: 1; min-width: 0; }

/* topbar */
.pl-topbar {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 18px; flex-wrap: wrap; gap: 10px;
}
.pl-results-pill {
  font-family: 'inter', sans-serif;
  font-size: .8rem; font-weight: 600; color: #64748b;
  background: #f8fafc; border: 1px solid #eef1f6;
  border-radius: 20px; padding: 7px 15px;
  display: inline-flex; align-items: center; gap: 6px;
}
.pl-results-pill strong { color: #1E7A8C; font-weight: 600; }
.pl-results-pill i { color: #1E7A8C; font-size: .72rem; }

.pl-sort-wrap { display: flex; align-items: center; gap: 8px; }
.pl-sort-lbl { font-size: .8rem; font-weight: 600; color: #64748b; white-space: nowrap; }
.pl-sort-select {
  font-family: 'inter', sans-serif;
  border: 1.5px solid #e2e8f0; border-radius: 9px;
  padding: 8px 34px 8px 13px;
  font-size: .8rem; font-weight: 600; color: #475569;
  background: #fff; cursor: pointer; outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 11px center;
  min-width: 195px;
  transition: border-color .2s;
}
.pl-sort-select:focus { border-color: #1E7A8C; box-shadow: 0 0 0 3px rgba(30,122,140,.08); }

/* ════════════════════════════
   SKELETON
════════════════════════════ */
@keyframes plShimmer {
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
}
.pl-skel {
  background: linear-gradient(90deg,#f1f5f9 25%,#e8edf3 37%,#f1f5f9 63%);
  background-size: 1200px 100%;
  animation: plShimmer 1.4s ease infinite;
  border-radius: 8px;
}
.pl-skel-sidebar { padding: 18px; }
.pl-skel-head    { display: flex; justify-content: space-between; margin-bottom: 20px; }
.pl-skel-h       { height: 16px; width: 65px; }
.pl-skel-b       { height: 13px; width: 55px; }
.pl-skel-sec     { margin-bottom: 20px; }
.pl-skel-lbl     { height: 11px; width: 80px; margin-bottom: 10px; }
.pl-skel-inp     { height: 34px; width: 100%; border-radius: 9px; margin-bottom: 8px; }
.pl-skel-row     { display: flex; align-items: center; gap: 9px; margin-bottom: 8px; }
.pl-skel-box     { height: 15px; width: 15px; border-radius: 4px; flex-shrink: 0; }
.pl-skel-txt     { height: 11px; flex: 1; }

.pl-skel-card    { background: #fff; border-radius: 13px; border: 1px solid #eef1f6; overflow: hidden; }
.pl-skel-img     { height: 195px; width: 100%; }
.pl-skel-body    { padding: 13px; }
.pl-skel-cat     { height: 10px; width: 50px;  margin-bottom: 9px;  border-radius: 5px; }
.pl-skel-tit     { height: 12px; width: 100%; margin-bottom: 5px;  border-radius: 5px; }
.pl-skel-tit2    { height: 12px; width: 65%;  margin-bottom: 9px;  border-radius: 5px; }
.pl-skel-price   { height: 16px; width: 68px; margin-bottom: 9px;  border-radius: 5px; }
.pl-skel-stars   { height: 11px; width: 66px; margin-bottom: 11px; border-radius: 5px; }
.pl-skel-cta     { height: 37px; width: 100%; border-radius: 8px; }

/* ════════════════════════════
   MOBILE STICKY BOTTOM BAR
════════════════════════════ */
.pl-mobile-bar {
  display: none;
  position: fixed;
    bottom: 1px;
    left: 0;
    right: 0;
    z-index: 0;
    background: #fff;
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -3px 16px rgba(0, 0, 0, .09);
    padding: 9px 14px;
    padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
}
.pl-mobile-bar-inner { display: flex; gap: 10px;align-items:center}

.pl-mob-btn {
  flex: 1;                    /* each button exactly 50% */
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px 10px;
  border-radius: 12px;
  font-family: 'inter', sans-serif;
  font-size: .88rem; font-weight: 700;
  cursor: pointer; border: none;
  transition: all .2s;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.pl-mob-btn--filter {
  background: #1E7A8C; color: #fff;
  box-shadow: 0 4px 14px rgba(30,122,140,.28);
}
.pl-mob-btn--filter:active { background: #155f6e; transform: scale(.97); }
.pl-mob-btn--sort {
  background: #fff; color: #0f172a;
  border: 1.5px solid #e2e8f0;
}
.pl-mob-btn--sort:active { background: #f8fafc; transform: scale(.97); }
.pl-mob-badge {
  position: absolute; top: 7px; right: calc(50% - 38px);
  background: #f4a623; color: #fff;
  font-size: .6rem; font-weight: 600;
  min-width: 17px; height: 17px; border-radius: 17px;
  display: flex; align-items: center; justify-content: center;
  padding: 0 4px; border: 2px solid #fff;
}

/* ════════════════════════════
   OVERLAY  (shared)
   pointer-events:none when hidden — CRITICAL FIX
   stops invisible overlay eating tap events
════════════════════════════ */
.pl-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.46);
  z-index: 1040;
  opacity: 0;
  pointer-events: none;          /* ← KEY: no clicks when hidden */
  transition: opacity .27s ease;
  backdrop-filter: blur(2px);
}
.pl-overlay.on {
  opacity: 1;
  pointer-events: auto;         /* ← allow clicks only when visible */
}

/* ════════════════════════════
   FILTER DRAWER
════════════════════════════ */
.pl-drawer {
  position: fixed;
  top: 0; left: 0; bottom: 0;
  width: min(310px, 86vw);
  background: #fff;
  z-index: 1050;
  display: flex; flex-direction: column;
  transform: translateX(-100%);
  transition: transform .3s cubic-bezier(.4,0,.2,1);
  box-shadow: 8px 0 36px rgba(0,0,0,.13);
  /* CRITICAL: will-change improves paint, avoids click dead zones */
  will-change: transform;
}
.pl-drawer.open { transform: translateX(0); }

.pl-drawer-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px; border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
  background: #fff;
}
.pl-drawer-head-title {
  font-size: .97rem; font-weight: 600;
  color: #0f172a; display: flex; align-items: center; gap: 7px;
}
.pl-drawer-head-title i { color: #1E7A8C; }
.pl-drawer-x {
  width: 30px; height: 30px; border-radius: 8px;
  border: 1.5px solid #e2e8f0; background: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: #64748b; font-size: .85rem;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.pl-drawer-x:active { background: #fef2f2; border-color: #fca5a5; color: #ef4444; }

/* drawer body — scrollable */
.pl-drawer-body {
  flex: 1;
  overflow-y: auto;             /* ← scrollable */
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;   /* smooth iOS scroll */
  overscroll-behavior: contain;
  scrollbar-width: thin; scrollbar-color: #e2e8f0 transparent;
}
.pl-drawer-body::-webkit-scrollbar { width: 3px; }
.pl-drawer-body::-webkit-scrollbar-thumb { background: #e2e8f0; }

/* drawer footer */
.pl-drawer-foot {
  flex-shrink: 0; padding: 12px 18px;
  border-top: 1px solid #f1f5f9;
  display: flex; gap: 10px; background: #fff;
}
.pl-foot-reset {
  flex: 1; padding: 11px; border-radius: 11px;
  border: 1.5px solid #e2e8f0; background: #fff;
  color: #64748b; font-family: 'inter', sans-serif;
  font-size: .83rem; font-weight: 600; cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.pl-foot-apply {
  flex: 2; padding: 11px; border-radius: 11px;
  background: linear-gradient(135deg,#1E7A8C,#238495);
  color: #fff; border: none;
  font-family: 'inter', sans-serif;
  font-size: .85rem; font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(30,122,140,.22);
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

/* ════════════════════════════
   SORT BOTTOM SHEET
════════════════════════════ */
.pl-sort-sheet {
  position: fixed; bottom: 0; left: 0; right: 0;
  z-index: 1050; background: #fff;
  border-radius: 18px 18px 0 0;
  transform: translateY(100%);
  transition: transform .3s cubic-bezier(.4,0,.2,1);
  box-shadow: 0 -6px 36px rgba(0,0,0,.12);
  will-change: transform;
  max-height: 70vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
.pl-sort-sheet.open { transform: translateY(0); }

.pl-sort-handle { width: 34px; height: 4px; border-radius: 4px; background: #e2e8f0; margin: 11px auto 0; }
.pl-sort-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 13px 18px; border-bottom: 1px solid #f1f5f9; position: sticky; top: 0; background: #fff;
}
.pl-sort-title { font-size: .97rem; font-weight: 600; color: #0f172a; }

.pl-sort-list { padding: 6px 0 20px; }
.pl-sort-row {
  display: flex; align-items: center; gap: 13px;
  padding: 14px 18px; cursor: pointer;
  transition: background .14s;
  border-bottom: 1px solid #fafafa;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.pl-sort-row:last-child { border-bottom: none; }
.pl-sort-row:active { background: #f8fafc; }
.pl-sort-row.active { background: #e8f5f2; }

.pl-sort-radio {
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid #cbd5e1; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: all .18s;
}
.pl-sort-row.active .pl-sort-radio { border-color: #1E7A8C; background: #1E7A8C; }
.pl-sort-dot { width: 6px; height: 6px; border-radius: 50%; background: #fff; opacity: 0; }
.pl-sort-row.active .pl-sort-dot { opacity: 1; }
.pl-sort-txt { font-size: .87rem; font-weight: 500; color: #475569; }
.pl-sort-row.active .pl-sort-txt { color: #155f6e; font-weight: 700; }

/* ════════════════════════════
   RESPONSIVE
════════════════════════════ */
@media (max-width: 991px) {
  .pl-sidebar    { display: none; }   /* hidden on mobile — use drawer */
  .pl-mobile-bar { display: block; }
  .pl-main       { padding-bottom: 80px; } /* space for sticky bar */
}
@media (min-width: 992px) {
  .pl-mobile-bar    { display: none !important; }
  .pl-overlay       { display: none !important; }
  .pl-drawer        { display: none !important; }
  .pl-sort-sheet    { display: none !important; }
}
@media (max-width: 575px) {
  .pl-topbar { flex-direction: column; align-items: flex-start; }
  .pl-sort-select { min-width: unset; width: 100%; }
}
@media (max-width: 575.98px) {
  .add_cart_mobile, .mobile_filters {
      height: 68px;
  }
}
`;

/* ═══════════════════════════════════════════════
   SKELETON COMPONENTS
═══════════════════════════════════════════════ */
const SkelSection = () => (
  <div className="pl-skel-sec">
    <div className="pl-skel pl-skel-lbl" />
    <div className="pl-skel pl-skel-inp" />
    {[0, 1, 2, 3].map(i => (
      <div className="pl-skel-row" key={i}>
        <div className="pl-skel pl-skel-box" />
        <div className="pl-skel pl-skel-txt" style={{ width: `${52 + i * 10}%` }} />
      </div>
    ))}
  </div>
);

const SkelSidebar = () => (
  <div className="pl-skel-sidebar">
    <div className="pl-skel-head">
      <div className="pl-skel pl-skel-h" />
      <div className="pl-skel pl-skel-b" />
    </div>
    <SkelSection /><SkelSection /><SkelSection />
  </div>
);

const SkelCard = () => (
  <div className="pl-skel-card">
    <div className="pl-skel pl-skel-img" />
    <div className="pl-skel-body">
      <div className="pl-skel pl-skel-cat" />
      <div className="pl-skel pl-skel-tit" />
      <div className="pl-skel pl-skel-tit2" />
      <div className="pl-skel pl-skel-price" />
      <div className="pl-skel pl-skel-stars" />
      <div className="pl-skel pl-skel-cta" />
    </div>
  </div>
);

const SkelGrid = () => (
  <div className="grid-4 mb-xl-5 mb-3">
    {[...Array(8)].map((_, i) => <SkelCard key={i} />)}
  </div>
);

/* ═══════════════════════════════════════════════
   ACCORDION SECTION
═══════════════════════════════════════════════ */
const AccSection = ({ label, icon, defaultOpen, activeCount, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const bodyRef = useRef(null);
  return (
    <div className="pl-acc-section">
      <button className="pl-acc-btn" onClick={() => setOpen(p => !p)} type="button">
        <span className="pl-acc-label">
          <i className={icon} />
          {label}
          {activeCount > 0 && <span className="pl-acc-badge">{activeCount}</span>}
        </span>
        <span className={`pl-acc-chevron ${open ? "open" : ""}`}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <div
        ref={bodyRef}
        className="pl-acc-body"
        style={{ maxHeight: open ? `${bodyRef.current?.scrollHeight || 500}px` : "0px" }}
      >
        <div className="pl-acc-inner">{children}</div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   FILTER SEARCH BOX
═══════════════════════════════════════════════ */
const FSrch = ({ value, onChange, placeholder }) => (
  <div className="pl-fsearch">
    <i className="fa-solid fa-magnifying-glass pl-fsearch-icon" />
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
    />
    {value && (
      <button
        className="pl-fsearch-clear"
        onClick={() => onChange({ target: { value: "" } })}
        type="button"
      >
        <i className="fa-solid fa-xmark" />
      </button>
    )}
  </div>
);

/* ═══════════════════════════════════════════════
   CHECKBOX ITEM
═══════════════════════════════════════════════ */
const ChkItem = ({ id, value, checked, onChange, onClose, label, count }) => (
  <label
    className={`pl-chk-item ${checked ? "active" : ""}`}
    htmlFor={id}
    style={{ display: "flex" }}
  >
    <input
      type="checkbox"
      id={id}
      value={value}
      checked={checked}
      onChange={(e) => { onChange(e); onClose?.(); }}
    />
    <span className="pl-chk-box">
      {checked && (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
          <polyline points="2 6.5 5 9.5 10 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
    <span className="pl-chk-lbl">{label}</span>
    {count !== undefined && <span className="pl-chk-cnt">{count}</span>}
  </label>
);

/* ═══════════════════════════════════════════════
   FILTER PANEL  (used in both sidebar + drawer)
═══════════════════════════════════════════════ */
const FilterPanel = ({
  categories, brands, models,
  searchCat, setSearchCat,
  searchBra, setSearchBra,
  searchMod, setSearchMod,
  selectedCat, selectedBra, selectedMod,
  onCatChange, onBraChange, onModChange,
  handleRangeChange, handleRangeAfter,
  onClearAll,
  isMobile = false,
  sfx = "",
  onClose,
}) => {
  const defaultOpen = !isMobile;

  const filtCat = categories.filter(c => c.name.toLowerCase().includes(searchCat.toLowerCase()));
  const filtBra = brands.filter(b => b.name.toLowerCase().includes(searchBra.toLowerCase()));
  const filtMod = models.filter(m => m.name.toLowerCase().includes(searchMod.toLowerCase()));
  const totalActive = selectedCat.length + selectedBra.length + selectedMod.length;

  const removeChip = (type, id) => {
    const fakeEvt = { target: { value: id, checked: false } };
    if (type === "cat") onCatChange(fakeEvt);
    if (type === "bra") onBraChange(fakeEvt);
    if (type === "mod") onModChange(fakeEvt);
    onClose?.();
  };

  return (
    <div>
      {/* header */}
      <div className="pl-sb-head mx-3 d-lg-flex d-none py-3">
        <span className="pl-sb-title">
          <i className="fa-solid fa-sliders" />
          Filters
          {totalActive > 0 && <span className="pl-total-badge">{totalActive}</span>}
        </span>
        {totalActive > 0 && (
          <button className="pl-clear-btn my-2 mx-3" onClick={onClearAll} type="button">
            <i className="fa-solid fa-rotate-left" />Clear All
          </button>
        )}
      </div>

      {/* chips */}
      {totalActive > 0 && (
        <div className="pl-chips">
          {selectedCat.map(id => {
            const c = categories.find(x => String(x.id) === id); return c ? (
              <span className="pl-chip" key={id}>{c.name}
                <button onClick={() => removeChip("cat", id)} type="button"><i className="fa-solid fa-xmark" /></button>
              </span>) : null;
          })}
          {selectedBra.map(id => {
            const b = brands.find(x => String(x.id) === id); return b ? (
              <span className="pl-chip" key={id}>{b.name}
                <button onClick={() => removeChip("bra", id)} type="button"><i className="fa-solid fa-xmark" /></button>
              </span>) : null;
          })}
          {selectedMod.map(id => {
            const m = models.find(x => String(x.id) === id); return m ? (
              <span className="pl-chip" key={id}>{m.name}
                <button onClick={() => removeChip("mod", id)} type="button"><i className="fa-solid fa-xmark" /></button>
              </span>) : null;
          })}
        </div>
      )}

      {/* Category */}
      <AccSection label="Category" icon="fa-solid fa-tag" defaultOpen={defaultOpen} activeCount={selectedCat.length}>
        <FSrch value={searchCat} onChange={e => setSearchCat(e.target.value)} placeholder="Search category…" />
        <div className="pl-chk-list">
          {filtCat.length > 0
            ? filtCat.map(cat => (
              <ChkItem key={cat.id} id={`cat_${cat.id}${sfx}`} value={String(cat.id)}
                checked={selectedCat.includes(String(cat.id))}
                onChange={onCatChange} onClose={onClose}
                label={cat.name} count={cat.products_count} />
            ))
            : <p className="pl-empty-msg">No categories found</p>
          }
        </div>
      </AccSection>

      {/* Brand */}
      <AccSection label="Brand" icon="fa-solid fa-building" defaultOpen={defaultOpen} activeCount={selectedBra.length}>
        <FSrch value={searchBra} onChange={e => setSearchBra(e.target.value)} placeholder="Search brand…" />
        <div className="pl-chk-list">
          {filtBra.length > 0
            ? filtBra.map(bra => (
              <ChkItem key={bra.id} id={`bra_${bra.id}${sfx}`} value={String(bra.id)}
                checked={selectedBra.includes(String(bra.id))}
                onChange={onBraChange} onClose={onClose}
                label={bra.name} count={bra.products_count} />
            ))
            : <p className="pl-empty-msg">No brands found</p>
          }
        </div>
      </AccSection>

      {/* Model */}
      <AccSection label="Model" icon="fa-solid fa-boxes-stacked" defaultOpen={defaultOpen} activeCount={selectedMod.length}>
        <FSrch value={searchMod} onChange={e => setSearchMod(e.target.value)} placeholder="Search model…" />
        <div className="pl-chk-list">
          {filtMod.length > 0
            ? filtMod.map(mod => (
              <ChkItem key={mod.id} id={`mod_${mod.id}${sfx}`} value={String(mod.id)}
                checked={selectedMod.includes(String(mod.id))}
                onChange={onModChange} onClose={onClose}
                label={mod.name} count={mod.products_count} />
            ))
            : <p className="pl-empty-msg">No models found</p>
          }
        </div>
      </AccSection>

      {/* Price Range */}
      <AccSection label="Price Range" icon="fa-solid fa-indian-rupee-sign" defaultOpen={defaultOpen}>
        <div className="pl-price-wrap">
          {/* <PriceRangePicker
            min={0} max={isMobile ? 200000 : 10000}
            step={isMobile ? 100 : 1}
            initialMin={0} initialMax={isMobile ? 200000 : 10000}
            currency="₹"
            onChange={handleRangeChange}
            onAfterChange={handleRangeAfter}
          /> */}
          {/* Price Range */}
          <div className="mx-2 pb-2">
            <PriceRangePicker
              min={0}
              max={200000}
              step={100}
              initialMin={0}
              initialMax={200000}
              currency="₹"
              onChange={handleRangeChange}
              onAfterChange={handleRangeAfter}
            />
          </div>
        </div>
      </AccSection>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */
const ProductList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(location.search);
  const search = urlParams.get("search");
  const type = urlParams.get("type");
  const { slug = "" } = useParams();

  /* product state */
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [productcount, setProductCount] = useState(0);
  const [sortBy, setSortBy] = useState("");
  const [lastPage, setLastPage] = useState(1);
  const [cartCount, setCartCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  /* filter data */
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);

  /* price */
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(10000);

  /* filter search */
  const [searchCat, setSearchCat] = useState("");
  const [searchBra, setSearchBra] = useState("");
  const [searchMod, setSearchMod] = useState("");

  /* selected filters */
  const [selectedCat, setSelectedCat] = useState([]);
  const [selectedBra, setSelectedBra] = useState([]);
  const [selectedMod, setSelectedMod] = useState([]);

  /* wishlist */
  const [wishlisted, setWishlisted] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);

  /* mobile panel state */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const sortOptions = [
    { label: "Latest", value: "latest" },
    { label: "Alphabetically, A-Z", value: "az" },
    { label: "Alphabetically, Z-A", value: "za" },
    { label: "Best Selling", value: "best_selling" },
    { label: "Price, low to high", value: "price_low_high" },
    { label: "Price, high to low", value: "price_high_low" },
  ];

  /* device id */
  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) { deviceId = uuidv4(); localStorage.setItem("device_id", deviceId); }

  /* prevent body scroll when drawer/sort open */
  useEffect(() => {
    document.body.style.overflow = (drawerOpen || sortOpen) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen, sortOpen]);

  /* ── API calls (original logic unchanged) ── */
  const fetchProducts = async (
    page = 1, category = [], brands = [], models = [],
    min = 0, max = 10000, sortby, search, type
  ) => {
    setLoading(true);
    const resp = await fetch(
      `${API_BASE_URL}api/productList?categories=${category.join(",")}&brands=${brands.join(",")}&models=${models.join(",")}&minPrice=${min}&maxPrice=${max}&sortby=${sortby}&category=${slug}&search=${search}&type=${type}&page=${page}`
    );
    const json = await resp.json();
    if (json.status) {
      setTotalPages(json.data.last_page);
      setProductCount(json.data.total);
      setProducts(json.data.data);
      setCurrentPage(json.data.current_page);
      setLastPage(json.data.last_page);
    }
    setLoading(false);
  };
 
useEffect(() => {
  // Reset all filters when slug/search/type changes
  setSelectedCat([]);
  setSelectedBra([]);
  setSelectedMod([]);
  setMinPrice(0);
  setMaxPrice(200000);
  setSortBy("");
  fetchProducts(1, [], [], [], 0, 100000, "latest", search, type);
}, [slug, search, type]); // ← slug, search, type as dependencies

  const fetchModelData = () => {
    let done = false;
    if (!done) {
      fetch(`${API_BASE_URL}api/modelData`).then(r => r.json())
        .then(d => setModels(Array.isArray(d) ? d : d.data || []))
        .catch(console.log);
    }
    return () => { done = true; };
  };
  const fetchBrandData = () => {
    let done = false;
    if (!done) {
      fetch(`${API_BASE_URL}api/brandData`).then(r => r.json())
        .then(d => setBrands(Array.isArray(d) ? d : d.data || []))
        .catch(console.log);
    }
    return () => { done = true; };
  };
  const fetchCategoryData = () => {
    let done = false;
    if (!done) {
      fetch(`${API_BASE_URL}api/categories_front`).then(r => r.json())
        .then(d => setCategories(Array.isArray(d) ? d : d.data || []))
        .catch(console.log);
    }
    return () => { done = true; };
  };


  useEffect(() => {
    fetch(`${API_BASE_URL}api/getCart`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_id: localStorage.getItem("device_id") }),
    })
      .then(r => r.json())
      .then(j => {
        const count = j.count ?? 0;
        setCartCount(count);
        // fire event — updates Header + MobileBottomNav simultaneously
        // window.dispatchEvent(new CustomEvent("cart:updated", { detail: { count } }));
      })
      .catch(e => console.error(e));
  }, []);

  useEffect(() => { fetchCategoryData(); fetchBrandData(); fetchModelData(); getWishlist(); }, []);

  const addToCart = (id, qty) => {
    fetch(`${API_BASE_URL}api/addToCart`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_id: deviceId, id, qty }),
    })
      .then(r => r.json())
      .then(json => {
        const count = Object.keys(json.cart).length;
        setCartCount(count);
        const el = document.querySelector(".cartcount");
        if (el) el.innerText = count;
        window.dispatchEvent(new CustomEvent("cart:updated", { detail: { count } }));
      })
      .catch(e => console.error("Add to cart error:", e));
  };

  const handleRangeChange = ([min, max]) => { console.log("live", min, max); };
  const handleRangeAfter = ([min, max]) => {
    setMinPrice(min); setMaxPrice(max);
    fetchProducts(1, selectedCat, selectedBra, selectedMod, min, max, sortBy, search, type);
  };

  const handleWishlist = async (e, id) => {
    e.preventDefault();
    if (!requireLogin(navigate, location, { message: "Please login to use your wishlist" })) return;
    const userid = localStorage.getItem("user_id");
    try {
      const res = await fetch(`${API_BASE_URL}api/wishlist/add`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: id, userid }),
      });
      if (res.status === 201) toast.success(" Added to wishlist");
      else if (res.status === 409) toast.success(" Wishlist Deleted");
      getWishlist();
    } catch (e) { console.error("Wishlist error:", e); }
  };

  const getWishlist = () => {
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/myWishlistData?userid=${userid}`)
      .then(r => r.json())
      .then(json => {
        if (json?.data) {
          setWishlist(Object.values(json.data));
          setWishlistCount(json.data.length);
        }
      })
      .catch(console.error);
  };

  /* ── filter handlers ── */
  const handleCatChange = (e) => {
    const v = e.target.value;
    setSelectedCat(prev => {
      const u = e.target.checked ? [...prev, v] : prev.filter(x => x !== v);
      fetchProducts(1, u, selectedBra, selectedMod, minPrice, maxPrice, sortBy, search, type);
      return u;
    });
  };
  const handleBraChange = (e) => {
    const v = e.target.value;
    setSelectedBra(prev => {
      const u = e.target.checked ? [...prev, v] : prev.filter(x => x !== v);
      fetchProducts(1, selectedCat, u, selectedMod, minPrice, maxPrice, sortBy, search, type);
      return u;
    });
  };
  const handleModChange = (e) => {
    const v = e.target.value;
    setSelectedMod(prev => {
      const u = e.target.checked ? [...prev, v] : prev.filter(x => x !== v);
      fetchProducts(1, selectedCat, selectedBra, u, minPrice, maxPrice, sortBy, search, type);
      return u;
    });
  };

  const handleClearAll = () => {
    setSelectedCat([]); setSelectedBra([]); setSelectedMod([]);
    setMinPrice(0); setMaxPrice(10000); setSortBy("");
    fetchProducts(1, [], [], [], 0, 100000, "latest", search, type);
  };

  /* sort: close sheet immediately then fetch */
  const handleSortChange = (value) => {
    setSortBy(value);
    setSortOpen(false);
    fetchProducts(1, selectedCat, selectedBra, selectedMod, minPrice, maxPrice, value, search, type);
  };

  const totalActive = selectedCat.length + selectedBra.length + selectedMod.length;

  const filterProps = {
    categories, brands, models,
    searchCat, setSearchCat,
    searchBra, setSearchBra,
    searchMod, setSearchMod,
    selectedCat, selectedBra, selectedMod,
    onCatChange: handleCatChange,
    onBraChange: handleBraChange,
    onModChange: handleModChange,
    handleRangeChange, handleRangeAfter,
    onClearAll: handleClearAll,
  };

  /* ══════════════════════════════════════
     RENDER
  ══════════════════════════════════════ */
  return (
    <>
      {/* <style>{STYLES}</style> */}
      <Header className="location-none" />

      {/* Breadcrumb */}
      <section className="cl-breadcrumb-bar py-xl-3 py-3">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <nav aria-label="breadcrumb">
               <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/">Home</Link>
                  </li>

                  <li className="breadcrumb-item active" aria-current="page">
                    Product List
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      <section className="product-list section-padding pt-0 pl-wrap mt-3">
        <div className="container-fluid px-lg-5">
          <div className="pl-row product-listing-row">

            {/* ══ DESKTOP SIDEBAR ══ */}
            <aside className="pl-sidebar">
              {loading && products.length === 0
                ? <SkelSidebar />
                : <FilterPanel {...filterProps} isMobile={false} sfx="" onClose={undefined} />
              }
            </aside>

            {/* ══ MAIN CONTENT ══ */}
            <div className="pl-main filter_options">

              {/* Topbar */}
              <div className="pl-topbar sort_by_wrapper search_results pb-3 mb-1">
                {loading
                  ? <div className="pl-skel" style={{ height: 32, width: 145, borderRadius: 20 }} />
                  : (
                    <div className="pl-results-pill showing_items_count">
                      <i className="fa-solid fa-layer-group" />
                      Showing <strong>{productcount}</strong> results
                    </div>
                  )
                }
                <div className="dropdown sort-dropdown d-lg-flex d-none align-items-center">
                  {loading
                    ? <div className="pl-skel" style={{ height: 37, width: 205, borderRadius: 9 }} />
                    : (
                      <div className="pl-sort-wrap">
                        <span className="pl-sort-lbl">Sort by:</span>
                        <select
                          className="pl-sort-select form-select"
                          value={sortBy}
                          onChange={e => handleSortChange(e.target.value)}
                        >
                          <option value="">Featured</option>
                          <option value="latest">Latest</option>
                          <option value="az">Alphabetically, A–Z</option>
                          <option value="za">Alphabetically, Z–A</option>
                          <option value="best_selling">Best Selling</option>
                          <option value="price_low_high">Price, low to high</option>
                          <option value="price_high_low">Price, high to low</option>
                        </select>
                      </div>
                    )
                  }
                </div>
              </div>

              {/* Grid */}
              {loading ? <SkelGrid /> : (
                <div className="pv-prod-grid mb-xl-5 mb-3">
                  {products.length > 0 ? products.map(product => (
                    <div className="pv-prod-card" key={product.id}>
                      {product.dprice ? (
                        <span className="pv-prod-tag"><i className="fa-solid fa-circle-check" /> {product.dprice}% Off</span>
                      ) : (
                        <span className="pv-prod-tag"><i className="fa-solid fa-circle-check" /> Hot Sale</span>
                      )}
                      <div className="pv-prod-img">
                        <Link to={`/product/${product.slug}`}>
                          <img
                            src={product.images
                              ? `${API_BASE_URL}public/productimages/${product.images.split(",")[0]}`
                              : `${API_BASE_URL}public/noimage.jpg`}
                            alt={product.name}
                          />
                        </Link>
                        <button
                          type="button"
                          className="pv-prod-wish"
                          onClick={e => handleWishlist(e, product.id)}
                          aria-label="Wishlist"
                        >
                          <i className={wishlist.some(item => item.id === product.id)
                            ? "fa-solid fa-heart"
                            : "fa-regular fa-heart"
                          } />
                        </button>
                      </div>
                      <div className="pv-prod-body">
                        <p className="pv-prod-cat">{product?.category_title?.name}</p>
                        <Link to={`/product/${product.slug}`}>
                          <h4 className="pv-prod-name">
                            {product.name} ({product?.weight} {product?.medicine_title?.name})
                          </h4>
                        </Link>
                        {(() => {
                          const prodRating = getProductRatingSummary(product, product?.average_rating, product?.review_count);
                          return (
                            <div className="pv-prod-rating">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <i key={n} className={n <= Math.round(prodRating.average || 0) ? "fa-solid fa-star" : "fa-regular fa-star"} />
                              ))}
                              <span>{prodRating.count > 0 ? `(${prodRating.count})` : "(0)"}</span>
                            </div>
                          );
                        })()}
                        <div className="pv-prod-price">
                          {product.dprice
                            ? <><b>₹{product.price - (product.price * product.dprice / 100)}</b><del>₹{product.price}</del></>
                            : <b>₹{product.price}</b>}
                        </div>
                        {(product?.stock_available === 'In Stock' && product?.sku !== null && product?.sku !== undefined && Number(product?.sku) > 0) ? (
                          <button
                            type="button"
                            className="pv-prod-cart"
                            onClick={(e) => {
                              flyToCart(e.currentTarget.closest(".pv-prod-card").querySelector(".pv-prod-img"));
                              addToCart(product.id, 1);
                            }}
                          >
                            <i className="fa-solid fa-cart-shopping" /> Add to Cart
                          </button>
                        ) : (
                          <button type="button" disabled className="pv-prod-cart pv-prod-oos">
                            <i className="fa-solid fa-cart-shopping" /> Out of Stock
                          </button>
                        )}
                      </div>
                    </div>
                  )) : (
                    <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "56px 20px", color: "#64748b" }}>
                      <i className="fa-solid fa-box-open" style={{ fontSize: "2.4rem", marginBottom: 10, display: "block", color: "#e2e8f0" }} />
                      <p>No products found for this category.</p>
                    </div>
                  )}
                </div>
              )}

              <Pagination
                className="pagination-custom"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) =>
                  fetchProducts(page, selectedCat, selectedBra, selectedMod, minPrice, maxPrice, sortBy, search, type)
                }
              />
            </div>
            {/* end main */}

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          MOBILE  —  sticky bottom bar
          Both buttons flex:1 = exactly 50% each
      ══════════════════════════════════════════ */}
      <div className="pl-mobile-bar mobile_filters">
        <div className="pl-mobile-bar-inner w-100">
          <button
            className="pl-mob-btn pl-mob-btn--filter rounded-pill"
            onClick={() => setDrawerOpen(true)}
            type="button"
          >
            <i className="fa-solid fa-sliders" />
            Filters
            {totalActive > 0 && <span className="pl-mob-badge">{totalActive}</span>}
          </button>
          <button
            className="pl-mob-btn pl-mob-btn--sort rounded-pill"
            onClick={() => setSortOpen(true)}
            type="button"
          >
            <i className="fa-solid fa-arrow-up-wide-short" />
            Sort By
            {sortBy && (
              <span className="round" style={{
                color: "#fff",
                fontSize: ".6rem", fontWeight: 800,
                padding: "1px 6px", borderRadius: 20, marginLeft: 4,
              }}>1</span>
            )}
          </button>
          <Link to="/cart" className="position-relative bottom-cart">
            <ShoppingCart className="text-white" />
            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
              <span className="cartcount text-white">{cartCount}</span>
              <span className="visually-hidden">unread messages</span>
            </span>
          </Link>

        </div>
      </div>

      {/* ══════════════════════════════════════════
          SHARED OVERLAY
          pointer-events:none when closed → products remain clickable
      ══════════════════════════════════════════ */}
      <div
        className={`pl-overlay ${(drawerOpen || sortOpen) ? "on" : ""}`}
        onClick={() => { setDrawerOpen(false); setSortOpen(false); }}
      />

      {/* ══════════════════════════════════════════
          FILTER DRAWER
          pl-drawer-body is overflow-y:auto + touch scroll
      ══════════════════════════════════════════ */}
      <div className={`pl-drawer ${drawerOpen ? "open" : ""}`}>
        <div className="pl-drawer-head">
          <span className="pl-drawer-head-title">
            <i className="fa-solid fa-sliders" />
            Filters
            {totalActive > 0 && <span className="pl-total-badge" style={{ marginLeft: 4 }}>{totalActive}</span>}
          </span>
          <button className="pl-drawer-x" onClick={() => setDrawerOpen(false)} type="button">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* scrollable body */}
        <div className="pl-drawer-body">
          <FilterPanel
            {...filterProps}
            isMobile={true}
            sfx="_mob"
            onClose={() => setDrawerOpen(false)}
          />
        </div>

        <div className="pl-drawer-foot">
          <button
            className="pl-foot-reset"
            onClick={() => { handleClearAll(); setDrawerOpen(false); }}
            type="button"
          >
            Reset
          </button>
          <button
            className="pl-foot-apply"
            onClick={() => setDrawerOpen(false)}
            type="button"
          >
            Done {totalActive > 0 ? `(${totalActive})` : ""}
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          SORT BOTTOM SHEET
          Closes instantly on row tap via handleSortChange
      ══════════════════════════════════════════ */}
      <div className={`pl-sort-sheet ${sortOpen ? "open" : ""}`}>
        <div className="pl-sort-handle" />
        <div className="pl-sort-head">
          <span className="pl-sort-title">Sort by</span>
          <button className="pl-drawer-x" onClick={() => setSortOpen(false)} type="button">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="pl-sort-list sort_by_list">
          {sortOptions.map(opt => (
            <div
              key={opt.value}
              className={`pl-sort-row form-check ${sortBy === opt.value ? "active" : ""}`}
              onClick={() => handleSortChange(opt.value)}
            >
              <div className="pl-sort-radio">
                <div className="pl-sort-dot" />
              </div>
              <span className="pl-sort-txt form-check-label">{opt.label}</span>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </>
  );
};  

export default ProductList;